import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";
import { Auth0Provider, withAuth0 } from "@auth0/auth0-react";
import { getConfig } from "./common/auth/config.js";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "./common/components/chakra/provider.tsx";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "./common/components/chakra/toaster.tsx";
import "./common/prototypes/pluck.tsx";
import NiceModal from "@ebay/nice-modal-react";
import { PostHogProvider } from "posthog-js/react";
import GlobalBoundary from "./common/components/GlobalBoundary.tsx";

const queryClient = new QueryClient();

const config = getConfig();

const onRedirectCallback = (appState: unknown) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window?.navigateTo(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    appState && appState?.returnTo
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        appState.returnTo
      : window.location.pathname,
  );
};

const providerConfig = {
  domain: config.domain,
  clientId: config.clientId,
  cacheLocation: "localstorage",
  useRefreshTokens: true,
  onRedirectCallback,
  authorizationParams: {
    redirect_uri: window.location.origin,
    ...(config.audience ? { audience: config.audience } : null),
  },
};
const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: "2025-05-24",
};

const PreloadAppUserState = withAuth0(({ auth0, children }) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  if (window.Cypress) {
    const userToken = window.localStorage.getItem("auth0Cypress");
    const userData = JSON.parse(userToken);

    auth0.user = userData?.body?.decodedToken?.user;
    auth0.getAccessTokenSilently = async () =>
      new Promise(userData?.body?.access_token);
    auth0.isAuthenticated = !!auth0.user;
  } else {
    const auth0Creds = window.localStorage.getItem("auth0creds");
    if (auth0Creds) {
      const userData = JSON.parse(auth0Creds);
      auth0.user = userData?.body?.decodedToken?.user;
      auth0.getAccessTokenSilently = async () =>
        new Promise(userData?.body?.access_token);
      auth0.isAuthenticated = !!auth0.user;
      const oldLogout = auth0.logout;
      auth0.logout = () => {
        window.localStorage.removeItem("auth0creds");
        oldLogout();
      };
    }
  }

  return <>{children}</>;
});

// eslint-disable-next-line react-refresh/only-export-components
function Main() {
  return (
    <>
      <BrowserRouter>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
        {/* @ts-expect-error*/}
        <Auth0Provider {...providerConfig}>
          <PreloadAppUserState>
            <Provider>
              <QueryClientProvider client={queryClient}>
                <NiceModal.Provider>
                  <App />
                  <Toaster />
                </NiceModal.Provider>
              </QueryClientProvider>
            </Provider>
          </PreloadAppUserState>
        </Auth0Provider>
      </BrowserRouter>
    </>
  );
}

const isLocal =
  window.location.host.includes("localhost") ||
  window.location.host.includes("192.168.0.143");

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <GlobalBoundary>
      {!isLocal && (
        <PostHogProvider
          apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          options={options}
        >
          <Main />
        </PostHogProvider>
      )}
      {isLocal && <Main />}
    </GlobalBoundary>
  </StrictMode>,
);
